import Alert from "components/common/Alert";
import { useEffect, useMemo, useRef, useState } from "react";
import { getExhibitorDropdown } from "api/exhibitor.api";
import { useProductCategory } from "components/productCategory/hooks/useProductCategory";
import { useCurrency } from "components/currency/hooks/useCurrency";
import { getImageUrl } from "utils/imageHelper";
import PageLayout from ".././PageFromLayout";
import {
  InputField,
  SelectField,
  ImageUploadField,
  TextareaField,
} from ".././FormFields";
import { FiArrowRight } from "react-icons/fi";
import FormSection from ".././FormSection";
import { validateImageFile } from "utils/common";
export default function ProductsForm({
  initialData = null,
  onSubmit,
  submitLabel,
  onBack,
}) {
  const { categories, loadCategoriesByOrganizer } = useProductCategory();

  const [exhibitors, setExhibitors] = useState([]);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const alertRef = useRef(null);
  const { data: currencies, loadCurrencies } = useCurrency();
  const [role, setRole] = useState("admin");

  const [formData, setFormData] = useState({
    company_id: "",
    exhibitor_id: "",
    product_category_id: "",
    product_name: "",
    price: "",
    currency_id: "",
    website: "",
    short_short_description: "",
    status: "active",
    is_sample: false,
    sample_start_date: "",
    sample_end_date: "",
    productImages: [],
    default_image: "",
  });

  const formatInputDateTimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");

    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());

    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
  };

  useEffect(() => {
    if (!initialData || !initialData._id) return;

    setFormData({
      company_id: initialData.company_id?._id || "",
      exhibitor_id: initialData.exhibitor_id?._id || "",
      product_category_id: initialData.product_category_id?._id || "",
      product_name: initialData.product_name || "",
      price: initialData.price || "",
      currency_id: initialData.currency_id?._id || "",
      website: initialData.website || "",
      short_description: initialData.short_description || "",
      status: initialData.status,
      is_sample: Boolean(initialData.is_sample),
      sample_start_date: initialData.sample_start_date
        ? formatInputDateTimeLocal(initialData.sample_start_date)
        : "",
      sample_end_date: initialData.sample_end_date
        ? formatInputDateTimeLocal(initialData.sample_end_date)
        : "",

      productImages: initialData.productImages || [],
      default_image: initialData.default_image
        ? JSON.stringify({ type: "existing", path: initialData.default_image })
        : "",
    });
  }, [initialData]);

  useEffect(() => {
    loadCurrencies();
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (userData) {
      setRole(userData.role);
    }
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("company_id");
    if (!companyId) return;
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (userData) {
      setRole(userData.role);
    }

    getExhibitorDropdown(companyId)
      .then((res) => {
        const exhibitors = (res.data || []).map((item) => ({
          value: item._id,
          label: item.title,
        }));

        setExhibitors(exhibitors);

        if (userData?.role === "exhibitor" && exhibitors.length > 0) {
          setFormData((p) => ({
            ...p,
            exhibitor_id: exhibitors[0].value,
          }));
        }
      })
      .catch(() => setExhibitors([]));

    loadCategoriesByOrganizer(companyId);
  }, []);

  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [alert]);

  const defaultMeta = useMemo(() => {
    try {
      return formData.default_image ? JSON.parse(formData.default_image) : null;
    } catch {
      return null;
    }
  }, [formData.default_image]);

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // 1. Validate all selected files (e.g. max 5 MB per image)
    const validationResults = await Promise.all(
      files.map((file) => validateImageFile(file,5))
    );

    // 2. Find if any file failed validation
    const invalidResult = validationResults.find((res) => !res.isValid);

    if (invalidResult) {
      // Set error message for the productImages field
      setErrors((prev) => ({
        ...prev,
        productImages: invalidResult.error,
      }));
      return; // Block adding invalid files to state
    }

    // 3. Clear previous errors if all files are valid
    setErrors((prev) => ({
      ...prev,
      productImages: "",
    }));

    // 4. Append validated files to formData
    setFormData((prev) => ({
      ...prev,
      productImages: [...(prev.productImages || []), ...files],
    }));
  };

  const removeImage = (index) => {
    setFormData((p) => {
      const imgs = [...p.productImages];
      const removed = imgs.splice(index, 1)[0];

      return {
        ...p,
        productImages: imgs,
        removed_images: [
          ...(p.removed_images || []),
          ...(typeof removed === "string" ? [removed] : []),
        ],
        default_image: defaultMeta?.path === removed ? "" : p.default_image,
      };
    });
  };

  const setDefaultImage = (img, index) => {
    setFormData((p) => ({
      ...p,
      default_image:
        img instanceof File
          ? JSON.stringify({ type: "new", index })
          : JSON.stringify({ type: "existing", path: img }),
    }));
  };

  const validate = () => {
    const e = {};

    if (!formData.exhibitor_id) e.exhibitor_id = "Exhibitor is required";
    if (!formData.product_category_id)
      e.product_category_id = "Product category is required";
    if (!formData.product_name?.trim())
      e.product_name = "Product name is required";
    if (!formData.price) e.price = "Price is required";

    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setAlert("Please fix the errors below");
      return;
    }

    setErrors({});
    setAlert("");

    const fd = new FormData();

    Object.entries(formData).forEach(([k, v]) => {
      if (
        k === "productImages" ||
        k === "removed_images" ||
        k === "default_image" ||
        k === "is_sample" ||
        k === "sample_start_date" ||
        k === "sample_end_date"
      )
        return;
      if (v !== null && v !== "") fd.append(k, v);
    });

    formData.productImages.forEach((img) => {
      if (img instanceof File) {
        fd.append("productImages", img);
      }
    });

    fd.append(
      "existing_images",
      JSON.stringify(
        formData.productImages.filter((i) => typeof i === "string")
      )
    );

    if (formData.removed_images?.length) {
      fd.append("removed_images", JSON.stringify(formData.removed_images));
    }

    if (formData.default_image) {
      fd.append("default_image", formData.default_image);
    }

    fd.append("is_sample", formData.is_sample);

    if (formData.is_sample) {
      fd.append(
        "sample_start_date",
        new Date(formData.sample_start_date).toISOString()
      );
      fd.append(
        "sample_end_date",
        new Date(formData.sample_end_date).toISOString()
      );
    }

    setSubmitting(true);
    try {
      await onSubmit(fd);
    } finally {
      setSubmitting(false);
    }
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
  };

  const pageTitle = initialData ? "Edit product" : "Create product";
  const pageSubTitle = initialData
    ? "Update the product details below."
    : "Add a new product company to the platform. Fill in the details below.";
  return (
    <PageLayout
      onBack={onBack}
      onSubmit={submit}
      submitText={
        initialData ? "Update Master Company" : "Create  Master Company"
      }
      loading={submitting}
    >
      <form onSubmit={submit} className="flex flex-col gap-6">
        {alert && (
          <div ref={alertRef}>
            <Alert type="error" message={alert} onClose={() => setAlert("")} />
          </div>
        )}
        <FormSection title="Product Details">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Exhibitor */}
            <SelectField
              label="Exhibitor"
              name="exhibitor_id"
              value={formData.exhibitor_id || ""}
              onChange={handleChange}
              options={exhibitors}
              placeholder="Search exhibitor..."
              isSearchable={true}
              required
              error={errors.exhibitor_id}
              isDisabled={role === "exhibitors"}
            />

            {/* Product Category */}
            <SelectField
              label="Product Category"
              name="product_category_id"
              value={formData.product_category_id || ""}
              onChange={handleChange}
              options={(categories || []).map((item) => ({
                value: item._id,
                label: item.title,
              }))}
              placeholder="Search product category..."
              isSearchable={true}
              required
              error={errors.product_category_id}
            />

            {/* Product Name */}
            <InputField
              label="Product Name"
              name="product_name"
              value={formData.product_name || ""}
              onChange={handleChange}
              placeholder="Enter product name"
              error={errors.product_name}
              required
            />

            {/* Currency & Price Group */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <SelectField
                label="Currency"
                name="currency_id"
                value={formData.currency_id || ""}
                onChange={handleChange}
                options={(currencies || []).map((item) => ({
                  value: item._id,
                  label: item.title,
                }))}
                placeholder="Search currency..."
                isSearchable={true}
                required
                error={errors.currency_id}
              />

              <InputField
                label="Price"
                type="number"
                name="price"
                value={formData.price || ""}
                onChange={handleChange}
                placeholder="Enter price"
                error={errors.price}
                required
              />
            </div>
            <InputField
              label="Website"
              name="website"
              value={formData.website || ""}
              onChange={handleChange}
              placeholder="Enter website"
              error={errors.website}
            />
            <SelectField
              label="Available Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "Inactive" },
              ]}
              placeholder="Search status..."
              error={errors.status}
            />
            <TextareaField
              label="Short Description"
              name="short_description"
              value={formData.short_description}
              onChange={handleChange}
              placeholder="Enter Description ..."
              rows={3}
              error={errors.short_description}
            />
          </div>
        </FormSection>
        <FormSection title="Product Images">
          <div className="grid grid-cols-1 ">
            <ImageUploadField
              label="Gallery Images"
              multiple={true}
              // preview={formData.productImages||[]} // Array of URLs: ["blob:http...", "blob:http..."]
              onUpload={handleImages}
              // onRemove={(index) => handleRemoveGalleryImage(index)}
              showRemove={true}
              name="productImages"
              error={errors.productImages}
              accept="image/jpeg, image/png, image/webp, image/heic, image/heif, .heic, .heif"
            />
            <div className="mt-2 grid grid-cols-2 gap-4 md:grid-cols-4">
              {formData.productImages.map((img, i) => {
                const src =
                  typeof img === "string"
                    ? getImageUrl(img)
                    : URL.createObjectURL(img);

                const isDefault =
                  (defaultMeta?.type === "new" && defaultMeta.index === i) ||
                  (defaultMeta?.type === "existing" &&
                    defaultMeta.path === img);

                return (
                  <div
                    key={i}
                    className={`relative rounded-xl border p-2 ${
                      isDefault ? "ring-2 ring-brand-500" : ""
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="h-32 w-full rounded-lg object-cover"
                    />

                    <div className="mt-2 flex justify-between">
                      <button
                        type="button"
                        onClick={() => setDefaultImage(img, i)}
                        className="text-xs text-blue-600"
                      >
                        Set Default
                      </button>

                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="text-xs text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </FormSection>
        <FormSection title="Sample Information">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <SelectField
              label="Is Sample Available"
              name="is_sample"
              value={formData.is_sample ? "yes" : "no"}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  is_sample: e.target.value === "yes",
                  sample_start_date: "",
                  sample_end_date: "",
                }))
              }
              options={[
                { label: "No", value: "no" },
                { label: "Yes", value: "yes" },
              ]}
              placeholder="Search sample..."
              error={errors.is_sample}
            />
            {formData.is_sample && (
              <>
                <InputField
                  label="Start Date & Time"
                  type="datetime-local"
                  name="sample_start_date"
                  value={formData.sample_start_date || ""}
                  onChange={handleChange}
                  placeholder="Enter start date"
                  error={errors.sample_start_date}
                  required
                />
                <InputField
                  label="Start Date & Time"
                  type="datetime-local"
                  name="sample_end_date"
                  value={formData.sample_end_date || ""}
                  onChange={handleChange}
                  placeholder="Enter end date"
                  error={errors.sample_end_date}
                  required
                />
              </>
            )}
          </div>
        </FormSection>
        {/* <Section title="Product Details"> */}
        {/* <SelectInput
            label="Exhibitor"
            required
            value={formData.exhibitor_id}
            error={errors.exhibitor_id}
            onChange={(e) =>
              setFormData((p) => ({ ...p, exhibitor_id: e.target.value }))
            }
          >
            <option value="">Select Exhibitor</option>
            {exhibitors.map((e) => (
              <option key={e._id} value={e._id}>
                {e.title}
              </option>
            ))}
          </SelectInput> */}

        {/* <SelectInput
            label="Product Category"
            required
            value={formData.product_category_id}
            error={errors.product_category_id}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                product_category_id: e.target.value,
              }))
            }
          >
            <option value="">Select Product Category</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.title}
              </option>
            ))}
          </SelectInput> */}
        {/* <div className="grid grid-cols-1 gap-5 md:col-span-2 md:grid-cols-4">
            <div className="md:col-span-2"></div>
          </div> */}

        {/* <div className="grid grid-cols-1 gap-5 md:col-span-2 md:grid-cols-4">
            <div className="md:col-span-2">
              <Input
                label="Product Name"
                required
                value={formData.product_name}
                error={errors.product_name}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, product_name: e.target.value }))
                }
              />
            </div>

            <SelectInput
              label="Currency"
              required
              value={formData.currency_id}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  currency_id: e.target.value,
                }))
              }
            >
              <option value="">Select Currency</option>

              {currencies.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.symbol} {c.title} ({c.code})
                  {c.is_deleted ? " (Inactive)" : ""}
                </option>
              ))}
            </SelectInput>

            <Input
              label="Price"
              type="number"
              required
              step="0.01"
              min="0"
              value={formData.price}
              error={errors.price}
              onChange={(e) =>
                setFormData((p) => ({ ...p, price: e.target.value }))
              }
            />
          </div> */}
        {/* 
          <Input
            label="Website"
            value={formData.website}
            onChange={(e) =>
              setFormData((p) => ({ ...p, website: e.target.value }))
            }
          />

          <SelectInput
            label="Is Sample Available"
            value={formData.is_sample ? "yes" : "no"}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                is_sample: e.target.value === "yes",
                sample_start_date: "",
                sample_end_date: "",
              }))
            }
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </SelectInput> */}

        {/* <Textarea
            label="Short Description"
            value={formData.short_description}
            onChange={(e) =>
              setFormData((p) => ({ ...p, short_description: e.target.value }))
            }
          /> */}
        {/* </Section> */}

        {/* <div className="flex justify-between">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl bg-red-500 px-6 py-3 text-white"
            >
              Back
            </button>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="rounded-xl bg-brand-500 px-6 py-3 text-white"
          >
            {submitLabel || (initialData ? "Update Product" : "Create Product")}
          </button>
        </div> */}
        {/* <div className="button-div mt-6 flex justify-between">
          {onBack && (
            <button type="button" onClick={onBack} className="btn-cancel">
              Back
            </button>
          )}
          <button type="submit" className="btn-primary flex items-center gap-2">
            <span>
              {submitLabel ||
                (initialData ? "Update Product" : "Create Product")}
            </span>
            <FiArrowRight size={16} />
          </button>
        </div> */}
      </form>
    </PageLayout>
  );
}
